import { useEffect, useState, useCallback } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  Inbox,
  LogOut,
  RefreshCw,
  Mail,
  Phone,
  Building2,
  Trash2,
  MessageSquare,
  ExternalLink,
  FileText,
  Download,
  Paperclip,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatBytes = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AdminDashboard() {
  const { user, loading, logout, authFetch } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setFetching(true);
    try {
      const { data } = await authFetch("/contacts");
      setItems(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error("Could not load submissions.");
    } finally {
      setFetching(false);
    }
  }, [authFetch, logout, navigate]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this submission? This cannot be undone.")) return;
    try {
      await authFetch(`/contacts/${id}`, { method: "DELETE" });
      setItems((xs) => xs.filter((x) => x.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Submission deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const downloadAttachment = async (s) => {
    if (!s.attachment_id) return;
    try {
      const { data } = await authFetch(`/contacts/${s.id}/attachment`, {
        method: "GET",
        responseType: "blob",
      });
      const blob = new Blob([data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = s.attachment_filename || `referral-${s.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Unable to download attachment.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--ew-accent)]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[var(--ew-bg)]">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-[#6E9C88]/15">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[var(--ew-accent)] text-white font-heading font-semibold">
              E
            </span>
            <div>
              <p className="font-heading text-sm font-semibold text-[var(--ew-text)]">
Everwell Admin
              </p>
              <p className="text-xs text-[var(--ew-text-muted)]">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-2 text-sm text-[var(--ew-text-2)] hover:text-[var(--ew-accent)] px-3 py-2 rounded-full transition-colors"
            >
              <ExternalLink className="h-4 w-4" strokeWidth={1.75} /> View site
            </Link>
            <button
              data-testid="admin-refresh"
              onClick={load}
              className="btn-secondary text-sm py-2 px-4"
              disabled={fetching}
            >
              <RefreshCw
                className={`h-4 w-4 ${fetching ? "animate-spin" : ""}`}
                strokeWidth={1.75}
              />
              Refresh
            </button>
            <button
              data-testid="admin-logout"
              onClick={() => {
                logout();
                navigate("/admin/login", { replace: true });
              }}
              className="btn-primary text-sm py-2 px-4"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.75} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="eyebrow">Submissions</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-medium text-[var(--ew-text)] mt-2">
              {items.length} enqu{items.length === 1 ? "iry" : "iries"}
            </h1>
          </div>
        </div>

        {fetching ? (
          <div className="mt-10 flex items-center justify-center py-20 text-[var(--ew-text-muted)]">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading submissions…
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="admin-empty"
            className="mt-10 ew-card p-12 text-center"
          >
            <span className="icon-badge mx-auto">
              <Inbox className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <h2 className="font-heading text-xl font-semibold mt-5 text-[var(--ew-text)]">
              No submissions yet
            </h2>
            <p className="mt-2 text-[var(--ew-text-2)]">
              Contact form submissions will appear here as soon as they arrive.
            </p>
          </div>
        ) : (
          <div className="mt-8 grid lg:grid-cols-12 gap-6">
            <ul
              data-testid="admin-list"
              className="lg:col-span-5 space-y-3"
            >
              {items.map((s) => {
                const active = selected?.id === s.id;
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => setSelected(s)}
                      data-testid={`admin-item-${s.id}`}
                      className={`w-full text-left ew-card p-5 transition-all ${
                        active
                          ? "border-[var(--ew-accent)] shadow-[0_14px_40px_rgba(46,80,65,0.12)]"
                          : ""
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-heading text-lg font-semibold text-[var(--ew-text)] truncate">
                          {s.name}
                        </p>
                        <span className="text-xs text-[var(--ew-text-muted)] shrink-0">
                          {formatDate(s.created_at)}
                        </span>
                      </div>
                      {s.organisation && (
                        <p className="text-sm text-[var(--ew-text-2)] mt-1 flex items-center gap-2">
                          <Building2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                          {s.organisation}
                        </p>
                      )}
                      <p className="text-sm text-[var(--ew-text-2)] mt-2 line-clamp-2">
                        {s.message}
                      </p>
                      <div className="mt-3 flex items-center gap-3 text-xs text-[var(--ew-text-muted)] flex-wrap">
                        <span className="inline-flex items-center gap-1.5">
                          <Mail className="h-3 w-3" /> {s.email}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <Phone className="h-3 w-3" /> {s.phone}
                        </span>
                        {s.attachment_id && (
                          <span className="inline-flex items-center gap-1.5 text-[var(--ew-accent)] font-medium">
                            <Paperclip className="h-3 w-3" /> PDF
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>

            <div className="lg:col-span-7 lg:sticky lg:top-24 self-start">
              {selected ? (
                <article
                  data-testid="admin-detail"
                  className="ew-card p-7 sm:p-8"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="eyebrow">Submission</p>
                      <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-[var(--ew-text)] mt-2 leading-tight">
                        {selected.name}
                      </h2>
                      <p className="text-sm text-[var(--ew-text-muted)] mt-1">
                        {formatDate(selected.created_at)} · kind:{" "}
                        <span className="font-medium text-[var(--ew-text-2)]">
                          {selected.kind}
                        </span>
                      </p>
                    </div>
                    <button
                      onClick={() => remove(selected.id)}
                      className="inline-flex items-center gap-2 text-sm text-red-600 hover:text-red-700 transition-colors"
                      data-testid="admin-delete"
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={1.75} /> Delete
                    </button>
                  </div>

                  <div className="mt-6 grid sm:grid-cols-2 gap-3">
                    <a
                      href={`mailto:${selected.email}`}
                      className="flex items-center gap-3 rounded-xl border border-[#6E9C88]/15 p-4 hover:border-[var(--ew-sage)]/60 transition-colors"
                    >
                      <span className="icon-badge">
                        <Mail className="h-4 w-4" strokeWidth={1.75} />
                      </span>
                      <span className="text-sm font-medium text-[var(--ew-text)] break-all">
                        {selected.email}
                      </span>
                    </a>
                    <a
                      href={`tel:${selected.phone}`}
                      className="flex items-center gap-3 rounded-xl border border-[#6E9C88]/15 p-4 hover:border-[var(--ew-sage)]/60 transition-colors"
                    >
                      <span className="icon-badge">
                        <Phone className="h-4 w-4" strokeWidth={1.75} />
                      </span>
                      <span className="text-sm font-medium text-[var(--ew-text)]">
                        {selected.phone}
                      </span>
                    </a>
                    {selected.organisation && (
                      <div className="flex items-center gap-3 rounded-xl border border-[#6E9C88]/15 p-4 sm:col-span-2">
                        <span className="icon-badge">
                          <Building2 className="h-4 w-4" strokeWidth={1.75} />
                        </span>
                        <span className="text-sm font-medium text-[var(--ew-text)]">
                          {selected.organisation}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="mt-6">
                    <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[var(--ew-text-muted)]">
                      Message
                    </p>
                    <p className="mt-3 text-[var(--ew-text)] leading-relaxed whitespace-pre-wrap">
                      {selected.message}
                    </p>
                  </div>

                  {selected.attachment_id && (
                    <div
                      data-testid="admin-attachment"
                      className="mt-6 flex items-center gap-3 rounded-2xl border border-[var(--ew-sage)]/30 bg-[var(--ew-bg-3)]/60 p-4"
                    >
                      <span className="icon-badge">
                        <FileText className="h-5 w-5" strokeWidth={1.75} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-[var(--ew-text)] truncate">
                          {selected.attachment_filename || "Referral.pdf"}
                        </p>
                        <p className="text-xs text-[var(--ew-text-muted)]">
                          {formatBytes(selected.attachment_size)} · referral PDF
                        </p>
                      </div>
                      <button
                        type="button"
                        data-testid="admin-attachment-download"
                        onClick={() => downloadAttachment(selected)}
                        className="btn-secondary text-sm py-2 px-4 whitespace-nowrap"
                      >
                        <Download className="h-4 w-4" strokeWidth={1.75} />
                        Download
                      </button>
                    </div>
                  )}

                  <div className="mt-7 flex flex-col sm:flex-row gap-3">
                    <a
                      href={`mailto:${selected.email}?subject=Re%3A%20Everwell%20enquiry`}
                      className="btn-primary"
                    >
                      <Mail className="h-4 w-4" strokeWidth={1.75} /> Reply by email
                    </a>
                    <a href={`tel:${selected.phone}`} className="btn-secondary">
                      <Phone className="h-4 w-4" strokeWidth={1.75} /> Call back
                    </a>
                  </div>
                </article>
              ) : (
                <div className="ew-card p-10 text-center">
                  <span className="icon-badge mx-auto">
                    <MessageSquare className="h-5 w-5" strokeWidth={1.75} />
                  </span>
                  <p className="mt-4 font-heading text-lg font-medium text-[var(--ew-text)]">
                    Select a submission
                  </p>
                  <p className="mt-2 text-[var(--ew-text-2)]">
                    Click an enquiry on the left to see the full message and
                    contact details.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
